import * as Redux from 'redux';
import {
  ProgramsActionTypes,
  ProgramsResponseData,
  LevelsActionTypes,
  LevelsResponseData,
  Schedule,
  ProgramMemberDetail,
  ScheduleActionTypes,
  ProgramMemberActionTypes
} from './Types';
import {
  GET_LEVELS,
  UPDATE_LEVELS,
  GET_PROGRAMS,
  REGISTER_PROGRAM,
  UPDATE_PROGRAM,
  DELETE_PROGRAM,
  UPDATE_SCHEDULES,
  UPDATE_PROGRAM_MEMBERS,
  GET_PROGRAM_MEMBERS
} from './Constants';
import { ReduxPromiseAction, SimpleResponse } from '../app/app.types';
import { callApi } from '../util/ApiCaller';
import { pickAuthOrImpersonationToken } from '../util/token.util';

export const getPrograms: Redux.ActionCreator<
  ReduxPromiseAction<
    ProgramsActionTypes, SimpleResponse<ProgramsResponseData>
  >
> = (): ReduxPromiseAction<ProgramsActionTypes, SimpleResponse<ProgramsResponseData>> => ({
  type: GET_PROGRAMS,
  payload: callApi(
    process.env.REACT_APP_API + '/program/list',
    true,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      }
    }
  )
});

export const createProgram: Redux.ActionCreator<
  ReduxPromiseAction<
    ProgramsActionTypes, SimpleResponse<ProgramsResponseData>
  >
> = (
  name: string,
  levels: Array<string>,
  schedules: Array<Schedule>,
  colorValue: string
): ReduxPromiseAction<
  ProgramsActionTypes, SimpleResponse<ProgramsResponseData>
> => ({
  type: REGISTER_PROGRAM,
  payload: callApi(
    process.env.REACT_APP_API + '/program',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({name, levels, schedules, colorValue})
    }
  )
});

export const updateProgram: Redux.ActionCreator<
  ReduxPromiseAction<
    ProgramsActionTypes, SimpleResponse<ProgramsResponseData>
  >
> = (
  id: number,
  name: string,
  colorValue: string,
): ReduxPromiseAction<
  ProgramsActionTypes, SimpleResponse<ProgramsResponseData>
> => ({
  type: UPDATE_PROGRAM,
  payload: callApi(
    process.env.REACT_APP_API + '/program',
    true,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({
        id,
        name,
        colorValue
      }),
    }
  ),
  meta: {id: id}
});

export const deleteProgram: Redux.ActionCreator<
  ReduxPromiseAction<
    ProgramsActionTypes, SimpleResponse<ProgramsResponseData>
  >
> = (id: number): ReduxPromiseAction<ProgramsActionTypes, SimpleResponse<ProgramsResponseData>> => ({
  type: DELETE_PROGRAM,
  payload: callApi(
    process.env.REACT_APP_API + '/program/' + id,
    true,
    {
      method: 'DELETE',
      headers: {
        'Authorization': pickAuthOrImpersonationToken()
      }
    }
  ),
  meta: {id: id}
});

export const getLevels: Redux.ActionCreator<
  ReduxPromiseAction<
    LevelsActionTypes, SimpleResponse<LevelsResponseData>
  >
> = (): ReduxPromiseAction<LevelsActionTypes, SimpleResponse<LevelsResponseData>> => ({
  type: GET_LEVELS,
  payload: callApi(
    process.env.REACT_APP_API + '/program/levels',
    true,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      }
    }
  )
});

export const updateProgramLevel: Redux.ActionCreator<
  ReduxPromiseAction<
    LevelsActionTypes, SimpleResponse<LevelsResponseData>
  >
> = (
  programId: number, 
  levels: string[]
): ReduxPromiseAction<LevelsActionTypes, SimpleResponse<LevelsResponseData>> => ({
  type: UPDATE_LEVELS,
  payload: callApi(
    process.env.REACT_APP_API + '/program/levels',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({
        programId,
        levels
      })
    }
  )
});

export const updateProgramSchedules: Redux.ActionCreator<
  ReduxPromiseAction<ScheduleActionTypes, SimpleResponse<{}>>> = (
  programId: number, 
  schedules: Schedule[]
): ReduxPromiseAction<ScheduleActionTypes, SimpleResponse<{}>> => ({
  type: UPDATE_SCHEDULES,
  payload: callApi(
    process.env.REACT_APP_API + '/program/schedules',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({
        programId: programId,
        schedules: schedules.map((s: Schedule) => {
          return {
            id: s.id,
            day: s.day,
            startHour: s.startHour,
            startMinute: s.startMinute,
            endHour: s.endHour,
            endMinute: s.endMinute
          };
        })
      })
    }
  )
});

export const updateProgramMembers: Redux.ActionCreator<
  ReduxPromiseAction<ProgramsActionTypes, SimpleResponse<{}>>> = (
  programMemberDetails: ProgramMemberDetail[]
): ReduxPromiseAction<ProgramsActionTypes, SimpleResponse<{}>> => ({
  type: UPDATE_PROGRAM_MEMBERS,
  payload: callApi(
    process.env.REACT_APP_API + '/program/members',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({
        programMemberDetails: programMemberDetails.map((p: ProgramMemberDetail) => {
          return {
            id: p.id,
            programId: p.programId,
            levelId: p.levelId,
            memberId: p.memberId,
            scheduleIds: p.scheduleIds,
            entityStatus: p.entityStatus,
            testHistoryDetails: p.testHistories,
          };
        })
      })
    }
  )
});

export const getProgramMembers: Redux.ActionCreator<
  ReduxPromiseAction<ProgramMemberActionTypes, SimpleResponse<{}>>> = (
  id: number
): ReduxPromiseAction<ProgramMemberActionTypes, SimpleResponse<{}>> => ({
  type: GET_PROGRAM_MEMBERS,
  payload: callApi(
    process.env.REACT_APP_API + '/program/members/' + id,
    true,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      }
    }
  )
});



// WEBPACK FOOTER //
// ./src/programs/Actions.tsx